// Per-table monotonic write clock.
//
// Bumped by the buffered manual change feed at DELIVERY time — i.e. at commit
// for transactional writes (the transaction buffer flushes post-COMMIT) and at
// statement completion for bare writes — synchronously, before the writer's
// continuation runs. Readers snapshot the clock when a fetch starts and compare
// on completion: a fetch that spanned a commit to any table it read is not
// allowed to come to rest as "fresh" (see the read-guard in
// `lib/database/change`). This is the ordering primitive that makes stale
// cache entries structurally impossible rather than merely unlikely — change
// events alone can always lose a race against an in-flight read's completion.

const versionByTable = new Map<string, number>();

export function bumpTableWriteClock(tableName: string): void {
  versionByTable.set(tableName, (versionByTable.get(tableName) ?? 0) + 1);
}

export function getTableWriteClock(tableName: string): number {
  return versionByTable.get(tableName) ?? 0;
}

/** Snapshot the clock for a set of tables (taken when a read starts). */
export function readTableWriteClockSnapshot(
  tableNames: readonly string[],
): ReadonlyMap<string, number> {
  const snapshot = new Map<string, number>();
  for (const tableName of tableNames) {
    snapshot.set(tableName, getTableWriteClock(tableName));
  }
  return snapshot;
}

/** True when any of the snapshotted tables has been written since the snapshot. */
export function hasTableWriteClockAdvanced(snapshot: ReadonlyMap<string, number>): boolean {
  for (const [tableName, version] of snapshot) {
    if (getTableWriteClock(tableName) > version) {
      return true;
    }
  }
  return false;
}
